
import {
    type FC,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { ErrorMessage, Field } from 'formik'
import styled from 'styled-components'

import { IconDownChevron } from 'assets/svg/sprite'
import colors from 'styles/colors'

import styles from './_FormikSelect.module.scss'

interface SelectOption {
    value: string
    label: string
    regions?: any[]
    data?: any
}

interface FormikSelectProps {
    customClass?: string
    label?: string
    fieldName: string
    options?: SelectOption[]
    selectedValue: string
    customClass2?: string
    autoComplete?: string

    searchable?: boolean
    searchPlaceholder?: string
    openUpwards?: boolean
    autoPosition?: boolean

    onChange: (selected: SelectOption) => void
    onTouched: () => void
}

export const FormikSelect: FC<FormikSelectProps> = ({
    label,
    fieldName,
    options,
    selectedValue,
    onChange,
    onTouched,
    customClass,
    customClass2,
    autoComplete,
    searchable = false,
    searchPlaceholder = '',
    openUpwards = false,
    autoPosition = false,
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
    const [search, setSearch] = useState('')

    const searchRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [shouldOpenUpwards, setShouldOpenUpwards] = useState(openUpwards)

    useLayoutEffect(() => {
        if (!autoPosition || !isDropdownOpen) return

        const updatePosition = () => {
            const element = containerRef.current
            if (!element) return

            const rect = element.getBoundingClientRect()

            const spaceBelow = window.innerHeight - rect.bottom
            const spaceAbove = rect.top

            const requiredSpace = 320

            setShouldOpenUpwards(
                spaceBelow < requiredSpace && spaceAbove > spaceBelow
            )
        }

        updatePosition()

        window.addEventListener('scroll', updatePosition, true)
        window.addEventListener('resize', updatePosition)

        return () => {
            window.removeEventListener('scroll', updatePosition, true)
            window.removeEventListener('resize', updatePosition)
        }
    }, [searchable, autoPosition, isDropdownOpen])
    
    const selectedLabel = useMemo(
        () =>
            options?.find(
                option => option.value === selectedValue
            )?.label,
        [options, selectedValue]
    )

    const filteredOptions = useMemo(() => {
        if (!searchable || !search.trim()) {
            return options ?? []
        }

        const query = search.trim().toLowerCase()

        return (options ?? []).filter(option =>
            `${option.label} ${option.value}`
                .toLowerCase()
                .includes(query)
        )
    }, [options, search, searchable])

    const closeSearch = () => {
        setIsDropdownOpen(false)
        setSearch('')
    }

    const openSearch = () => {
        setSearch('')
        setIsDropdownOpen(true)
    }

    const handleSelect = (option: SelectOption) => {
        onChange(option)
        setIsDropdownOpen(false)

        if (searchable) {
            setSearch('')
        }
    }

    const handleNativeChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const option = options?.find(
            item => item.value === event.target.value
        )

        if (!option) {
            return
        }

        onChange(option)
    }

    useEffect(() => {
        if (!searchable || !isDropdownOpen) {
            return
        }

        searchRef.current?.focus()
    }, [searchable, isDropdownOpen])

    useEffect(() => {
        if (!searchable || !isDropdownOpen) {
            return
        }

        const handleOutsideClick = (event: PointerEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(
                    event.target as Node
                )
            ) {
                closeSearch()
                onTouched()
            }
        }

        document.addEventListener(
            'pointerdown',
            handleOutsideClick
        )

        return () => {
            document.removeEventListener(
                'pointerdown',
                handleOutsideClick
            )
        }
    }, [searchable, isDropdownOpen, onTouched])

    return (
        <div
            ref={containerRef}
            className={`formikInput ${customClass ?? ''} ${
                styles.selectContainer
            } ${
                isDropdownOpen ? styles.selectDropdownOpen : ''
            } ${
                isDropdownOpen &&
                (autoPosition ? shouldOpenUpwards : searchable && openUpwards)
                    ? styles.selectDropdownUpwards
                    : ''
            }`}
            onClick={() => {
                // Preserve the original click behavior
                // for every non-searchable select.
                if (!searchable) {
                    setIsDropdownOpen(
                        previous => !previous
                    )
                }
            }}
            onBlur={event => {
                if (
                    event.relatedTarget &&
                    event.currentTarget.contains(
                        event.relatedTarget as Node
                    )
                ) {
                    return
                }

                setIsDropdownOpen(false)

                if (searchable) {
                    setSearch('')
                }

                onTouched()
            }}
            tabIndex={searchable ? -1 : 0}
        >
            <InputLabel
                $filled={
                    !!selectedValue ||
                    (searchable && isDropdownOpen)
                }
                $searchable={searchable}
            >
                {label}
            </InputLabel>

            {searchable ? (
                <div
                    className={styles.searchableControl}
                    onClick={event => {
                        event.stopPropagation()

                        if (isDropdownOpen) {
                            closeSearch()
                            searchRef.current?.blur()
                        } else {
                            openSearch()
                        }
                    }}
                >
                    <input
                        ref={searchRef}
                        type='search'
                        name='catalog-filter'
                        autoComplete='new-password'
                        autoCorrect='off'
                        autoCapitalize='off'
                        spellCheck={false}
                        className={styles.searchableInput}
                        value={
                            isDropdownOpen
                                ? search
                                : selectedLabel ?? ''
                        }
                        readOnly={!isDropdownOpen}
                        onChange={event => {
                            setSearch(event.target.value)
                        }}
                        onKeyDown={event => {
                            if (event.key === 'Escape') {
                                event.preventDefault()
                                event.stopPropagation()
                                closeSearch()
                                searchRef.current?.blur()
                            }

                            if (
                                event.key === 'Enter' &&
                                isDropdownOpen &&
                                filteredOptions.length === 1
                            ) {
                                event.preventDefault()
                                handleSelect(
                                    filteredOptions[0]
                                )
                            }

                            if (
                                event.key === 'ArrowDown' &&
                                !isDropdownOpen
                            ) {
                                event.preventDefault()
                                openSearch()
                            }
                        }}
                        onClick={event => {
                            // The parent searchableControl
                            // handles opening and closing.
                            event.stopPropagation()

                            if (isDropdownOpen) {
                                closeSearch()
                                searchRef.current?.blur()
                            } else {
                                openSearch()
                            }
                        }}
                        aria-label={label ?? 'Search options'}
                        aria-expanded={isDropdownOpen}
                        aria-haspopup='listbox'
                    />
                </div>
            ) : (
                // Original selected-value structure,
                // unchanged for existing usages.
                <div
                    className={`formikInput__field formikInput__field--filled ${
                        customClass2 ?? ''
                    } ${styles.formikSelect}`}
                >
                    <span>{selectedLabel}</span>

                    {isDropdownOpen && (
                        <div className={styles.dropdown}>
                            {options?.map(option => (
                                <div
                                    key={option.value}
                                    onClick={event => {
                                        event.stopPropagation()
                                        handleSelect(option)
                                    }}
                                >
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {searchable && isDropdownOpen && (
                <div
                    className={styles.dropdown}
                    onClick={event =>
                        event.stopPropagation()
                    }
                >
                    <div
                        className={styles.dropdownOptions}
                        onTouchStart={event =>
                            event.stopPropagation()
                        }
                        onTouchMove={event =>
                            event.stopPropagation()
                        }
                        role='listbox'
                        aria-label={label}
                    >
                        {filteredOptions.map(option => (
                            <div
                                key={option.value}
                                role='option'
                                aria-selected={
                                    selectedValue === option.value
                                }
                                onMouseDown={event => {
                                    event.preventDefault()
                                }}
                                onClick={event => {
                                    event.stopPropagation()
                                    handleSelect(option)
                                }}
                            >
                                {option.label}
                            </div>
                        ))}

                        {filteredOptions.length === 0 && (
                            <div>No matching results</div>
                        )}
                    </div>
                </div>
            )}

            <div
                className={styles.arrow}
                onClick={event => {
                    if (!searchable) {
                        return
                    }

                    event.stopPropagation()

                    if (isDropdownOpen) {
                        closeSearch()
                        searchRef.current?.blur()
                    } else {
                        openSearch()
                    }
                }}
            >
                <IconDownChevron />
            </div>

            {autoComplete ? (
                <NativeSelect
                    name={fieldName}
                    value={selectedValue}
                    autoComplete={autoComplete}
                    onChange={handleNativeChange}
                    tabIndex={-1}
                    aria-hidden='true'
                >
                    <option value='' />

                    {options?.map(option => (
                        <option
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </option>
                    ))}
                </NativeSelect>
            ) : null}

            <Field
                type='hidden'
                name={fieldName}
                value={selectedValue}
            />

            <ErrorMessageComponent>
                <ErrorMessage
                    component='div'
                    name={fieldName}
                />
            </ErrorMessageComponent>
        </div>
    )
}

interface InputProps {
    $filled: boolean
    $searchable?: boolean
}

const NativeSelect = styled.select`
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
    overflow: hidden;
`

const InputLabel = styled.label<InputProps>`
    letter-spacing: 0.02em;
    font-size: 1.2rem;
    line-height: 1.5rem;
    font-weight: 500;
    position: absolute;
    left: 2rem;
    color: #b0b0b0;
    pointer-events: none;
    transition: 0.2s all;
    transform: translateY(0);
    z-index: 1;

    ${props => !!props.$filled && 'top: 1.6rem;'}

    ${props =>
        props.$searchable &&
        `
            top: 50%;
            transform: translateY(-50%);
        `}

    ${props =>
        props.$searchable &&
        props.$filled &&
        `
            top: 1.6rem;
            transform: translateY(0);
        `}
`

const ErrorMessageComponent = styled.div`
    position: absolute;
    right: 0;
    font-weight: 500;
    letter-spacing: 0.02em;
    font-size: 1.2rem;
    line-height: 1.5rem;
    color: ${colors.red};
    margin-right: 3rem;
`
